/**
 * Navbar configuration — single source of truth for nav links, quick-action
 * icons, and the active/hover styling shared by the desktop nav, the desktop
 * dropdown menu, and the mobile slide-down panel.
 */
import { cn } from '../../../lib/utils';
import type { AppIconName } from '../../ui/AppIcon';

export interface NavLinkItem {
  label: string;
  href: string;
  imgSrc: string;
  /** Custom SVG icon (tintable) — takes precedence over the PNG `imgSrc`. */
  icon?: AppIconName;
  /** Scale override for icons with extra internal whitespace */
  iconClass?: string;
}

export interface NavGroupItem {
  kind: 'group';
  label: string;
  imgSrc: string;
  icon?: AppIconName;
  iconClass?: string;
  children: NavLinkItem[];
}

export type NavItem = (NavLinkItem & { kind: 'link' }) | NavGroupItem;


// Optical equalisation: these glyphs share a 24×24 viewBox but the outline-style
// ones (calendar, tour-guide, camping, flag) draw their art smaller inside it,
// so they read smaller than the bold glyphs (home, piano, medal, mortar-pestle).
// `scale-110` on just the small ones makes every icon LOOK the same size.
export const NAV_ITEMS: NavItem[] = [
  { kind: 'link', label: 'Home', href: '/', imgSrc: '/home.png', icon: 'home' },
  { kind: 'link', label: 'Ragis', href: '/ragis', imgSrc: '/turban.png', icon: 'piano' },
  {
    kind: 'group',
    label: 'Gurmat Events',
    imgSrc: '/calendar-check.png',
    icon: 'calendar-star',
    iconClass: 'scale-110',
    children: [
      { label: 'Samagam', href: '/events', imgSrc: '/calendar-check.png', icon: 'calendar-star', iconClass: 'scale-110' },
      { label: 'Amrit Sanchar', href: '/amrit-sanchar', imgSrc: '/cereal.png', icon: 'mortar-pestle' },
      { label: 'Yatra', href: '/yatra', imgSrc: '/calendar-check.png', icon: 'tour-guide', iconClass: 'scale-110' },
      { label: 'Gurmat Camp', href: '/gurmat-camps', imgSrc: '/calendar-check.png', icon: 'camping', iconClass: 'scale-110' },
    ],
  },
  { kind: 'link', label: 'Gurudwaras', href: '/gurudwaras', imgSrc: '/gurudwara-temple.png', icon: 'government-flag', iconClass: 'scale-110' },
  { kind: 'link', label: 'Sevadaars', href: '/sevadaars', imgSrc: '/medal.png', icon: 'medal' },
];

export const isNavActive = (pathname: string, href: string) =>
  href === '/' ? pathname === '/' : pathname.startsWith(href);

type NavLinkVariant = 'desktop' | 'menu' | 'mobile';

const INACTIVE_CLASSES: Record<NavLinkVariant, string> = {
  desktop: 'text-tx-secondary hover:text-tx-primary hover:bg-elevated',
  menu: 'text-tx-secondary hover:text-tx-primary hover:bg-elevated',
  mobile: 'text-tx-secondary hover:bg-elevated hover:text-tx-primary',
};

/**
 * Active/inactive color treatment for a nav link in any of the three surfaces.
 * `isSukhasan` is kept for call-site compatibility; token classes handle both
 * themes via CSS variables.
 */
export function navLinkClasses(active: boolean, _isSukhasan: boolean, variant: NavLinkVariant) {
  if (active) {
    return 'text-[var(--pill-badge-text)] bg-[var(--pill-badge-bg)]';
  }
  return cn(INACTIVE_CLASSES[variant]);
}
